import React, { useEffect } from 'react';
import { FileText, Book, Code, Shield, Zap, ChevronRight, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '../Footer';
import { PublicHeader } from '../PublicHeader';

export function Documentation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: 'Getting Started',
      icon: Book,
      items: [
        { title: 'Introduction to BidWin', href: '#introduction' },
        { title: 'Quick Start Guide', href: '#quickstart' },
        { title: 'Account Setup', href: '#account' },
        { title: 'Platform Overview', href: '#overview' }
      ]
    },
    {
      title: 'Core Features',
      icon: Zap,
      items: [
        { title: 'Contract Search & Filtering', href: '#search' },
        { title: 'AI-Powered Matching', href: '#matching' },
        { title: 'Proposal Generation', href: '#proposals' },
        { title: 'Analytics & Reporting', href: '#analytics' }
      ]
    },
    {
      title: 'Technical Guides',
      icon: Code,
      items: [
        { title: 'API Documentation', href: '#api' },
        { title: 'Webhooks', href: '#webhooks' },
        { title: 'Data Security', href: '#security' },
        { title: 'Integration Guide', href: '#integration' }
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-bw-app">
      <PublicHeader />
      
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative bg-bw-sidebar rounded-lg shadow-lg p-12 mb-12 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            </div>
            
            <div className="relative text-center text-white">
              <div className="flex items-center justify-center mb-6">
                <FileText className="h-12 w-12 text-bw-accent" />
              </div>
              <h1 className="text-4xl font-bold mb-4">Documentation</h1>
              <p className="text-white/90 max-w-2xl mx-auto">
                Comprehensive guides and documentation to help you make the most of BidWin's features and capabilities.
              </p>
              
              <div className="mt-8 max-w-xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-white/60 focus:ring-2 focus:ring-servet-red focus:border-transparent"
                  />
                  <Search className="absolute left-4 top-3.5 h-5 w-5 text-white/60" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sections.map((section) => (
              <div key={section.title} className="bg-bw-surface rounded-xl shadow-lg overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
                <div className="relative bg-bw-sidebar p-8">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
                  </div>

                  <div className="relative">
                    <section.icon className="h-8 w-8 text-white mb-4" />
                    <h3 className="text-xl font-bold text-white">{section.title}</h3>
                  </div>
                </div>

                <div className="p-6">
                  <ul className="space-y-3">
                    {section.items.map((item) => (
                      <li key={item.title}>
                        <Link
                          to={item.href}
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-bw-surface-subtle group"
                        >
                          <span className="text-bw-ink group-hover:text-bw-primary">
                            {item.title}
                          </span>
                          <ChevronRight className="h-4 w-4 text-bw-muted group-hover:text-bw-primary group-hover:translate-x-1 transition-all" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-bw-surface rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center text-bw-primary mb-4">
              <Shield className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-medium text-bw-ink text-center mb-2">
              Documentation Coming Soon
            </h3>
            <p className="text-bw-muted text-center">
              We're currently working on comprehensive documentation. Check back soon for detailed guides and tutorials.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}